import { logger } from '@adornis/base/logging.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import { generateLoginToken } from '@adornis/users/server/token.js';
import { differenceInDays, format } from 'date-fns';
import { getMailTemplateForInstitution } from '../api/mail-templates.js';
import { getParticipationByID } from '../api/participation-helper.js';
import { InstitutionMailTemplate, MailTemplateType, MailTemplateVars } from '../entities/mail-templates.js';
import { ParticipationWorkshop } from '../entities/ParticipationWorkshop.js';
import { User } from '../entities/user.js';
import type { Workshop } from '../entities/workshop.js';
import { assembleInstitutionMail } from '../mails/assemble-mail.js';
import { buildNewsletterOptinLink, buildRegistrationsHTML } from '../mails/mail-helpers.js';
import { institutionMailHint, institutionMailVariables } from '../mails/mail-template-catalog.js';
import { sendInstitutionEmail } from '../mails/sendInstitutionMail.js';
import { isDefined } from '../util.js';
import { getNotificationType, sendWhatsappWithEmailFallback } from './_helper.js';
import { getUserByID } from '../api/users.js';
import { getProductByID } from '../api/product.js';
import { getInstitutionByID } from '../api/institution.js';

defineMailTemplate(MailTemplateType.WARTELISTE_VORRUECK, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Wartelisten Update',
    content: `<p>Hallo {{title}} {{firstname}} {{lastname}},</p>
<p>F&uuml;r eine Ihrer Anmeldungen, bei denen Sie aktuell auf der Warteliste stehen, sind Sie einen Platz nach vorne gerutscht:</p>
<p>{{changed-registrations}}</p>
<p>Falls Sie den Termin nicht wahrnehmen k&ouml;nnen, bitten wir Sie, sich zeitnah in ihrer Anmeldeverwaltung von der Veranstaltung abzumelden. Andere Studierende auf der Warteliste profitieren dann von Ihrer Abmeldung.</p>
<p>Wir freuen uns, wenn wir Sie in Zukunft in einer unserer Veranstaltungen begr&uuml;&szlig;en d&uuml;rfen und empfehlen Ihnen hierf&uuml;r unser Vortrags- und Workshop-Programm auf <a href="https://anmeldung.uni-frankfurt.de">anmeldung.uni-frankfurt.de.</a></p>
<p>Viele Gr&uuml;&szlig;e</p>
<p>------------------------------------------------------------------</p>
<p>Hello {{title}} {{firstname}} {{lastname}},</p>
<p>there's an update for one of your registrations where you are currently on the waiting list:</p>
<p>{{changed-registrations}}</p>
<p>You are unable to attend? Please deregister and thereby enable other students to participate.</p>
<p>We look forward to welcoming you to one of our events in the future and recommand our lecture and workshop programme at <a href="https://anmeldung.uni-frankfurt.de">anmeldung.uni-frankfurt.de</a> for this purpose</p>
<p>Best regards</p>`,
    footer: '',
  },
  variables: institutionMailVariables(MailTemplateType.WARTELISTE_VORRUECK),
  attachments: [],
  displayName: 'Warteliste Vorrücken',
  editorHint: () => institutionMailHint(MailTemplateType.WARTELISTE_VORRUECK),
});

export async function notifyWartelisteVorrueck(participationID) {
  const participation = await getParticipationByID(participationID)({
    _id: 1,
    _class: 1,
    userID: 1,
    productID: 1,
    state: 1,
    waitingListPlace: 1,
  });
  if (!participation) throw new Error('Couldnt find participation with ID ' + participationID);

  const user = await getUserByID(participation.userID as string)({
    _id: 1,
    title: 1,
    firstName: 1,
    lastName: 1,
    email: 1,
    whatsappNotification: 1,
    whatsappPhone: 1,
    username: 1,
  });
  if (!user) throw new Error('no user is set');

  const product = await getProductByID(participation.productID as string)({
    _id: 1,
    _class: 1,
    name: 1,
    description: 1,
    sortingWeight: 1,
    hasPrice: 1,
    categoryID: 1,
    institutionID: 1,
    timeframeID: 1,
    publishDate: 1,
    topic: 1,
    language: 1,
    presenceType: 1,
    isCanceled: 1,
    appointments: { _class: 1, _id: 1, start: 1, end: 1, isNonBlocking: 1, location: 1, name: 1 },
    capacity: 1,
    hideCapacity: 1,
  });
  if (!product) throw new Error('no product found');
  (participation as any).product = product;

  const institutionID = product.institutionID;
  const mailTemplateType = MailTemplateType.WARTELISTE_VORRUECK;
  const mailTemplate = await getMailTemplateForInstitution(
    institutionID,
    mailTemplateType,
  )(InstitutionMailTemplate.allFields);

  const institution = await getInstitutionByID(institutionID)({ _id: 1 });
  if (!institution) throw new Error('institution not found ' + institutionID);

  const token = await generateLoginToken({
    userID: user._id,
    userClass: User._class,
    ttl: 1000 * 60 * 60 * 24 * 7 /* 7 days */,
  });

  const start = (participation.product as Workshop)?.appointments?.[0]?.start;
  const daysTillEvent = start ? differenceInDays(start, new Date()).toFixed(0) : undefined;
  let dates: string | undefined = (participation.product as Workshop)?.appointments
    ?.map(a => (a.start ? format(a.start, 'dd.MM.yyyy') : null))
    .filter(isDefined)
    .join(', ');
  if (!dates) dates = start ? format(start, 'dd.MM.yyyy') : undefined;

  const sendEmail = async () => {
    const mailVars = {
      [MailTemplateVars.TITLE]: user?.title ?? '',
      [MailTemplateVars.FIRST_NAME]: user?.firstName,
      [MailTemplateVars.LAST_NAME]: user?.lastName,
      [MailTemplateVars.EVENT_TITLE]: participation.product?.name,
      [MailTemplateVars.PRODUCT_NAME]: participation.product?.name,
      [MailTemplateVars.LOGIN_LINK]: InstitutionMailTemplate.mapLoginLinkVar(token),

      [MailTemplateVars.CHANGED_REGISTRATIONS]: buildRegistrationsHTML([
        {
          name: participation.product?.name ?? '',
          state: participation.state,
          topic: participation.product?.topic,
          language: participation.product?.language,
          waitingListPlace: participation instanceof ParticipationWorkshop ? participation.waitingListPlace : null,
          appointments: (participation.product as Workshop)?.appointments || [],
          englishVersion: false,
        },
      ]),
      [MailTemplateVars.CHANGED_REGISTRATIONS_EN]: buildRegistrationsHTML([
        {
          name: participation.product?.name ?? '',
          state: participation.state,
          topic: participation.product?.topic,
          language: participation.product?.language,
          waitingListPlace: participation instanceof ParticipationWorkshop ? participation.waitingListPlace : null,
          appointments: (participation.product as Workshop)?.appointments || [],
          englishVersion: true,
        },
      ]),
      // TODO: set this correctly
      [MailTemplateVars.OTHER_REGISTRATIONS]: InstitutionMailTemplate.mapOtherRegistrationsVar({
        participations: [],
        institution,
        products: [],
        englishVersion: false,
      }),
      [MailTemplateVars.EVENT_DATES]: dates,
      [MailTemplateVars.EVENT_LEAD_DAYS]: daysTillEvent,
      [MailTemplateVars.NEWSLETTER_OPTIN_LINK]: buildNewsletterOptinLink(token, 'de'),
      [MailTemplateVars.NEWSLETTER_OPTIN_LINK_EN]: buildNewsletterOptinLink(token, 'en'),
    };

    const { subject, content } = (await assembleInstitutionMail({
      template: mailTemplate,
      infos: mailVars as any,
    })) as { subject: string; content: any };

    if (!content) throw new Error('mail template has no content!');
    if (!subject) throw new Error('mail template has no subject!');

    const fullEmailRec = {
      email: user.email,
      institutionID,
      content,
      subject,
      attachmentPaths: [] as string[],
    };

    await sendInstitutionEmail(fullEmailRec);
  };

  const notificationType = await getNotificationType(user, mailTemplate, institutionID);
  // TODO: add whatsapp
  if (notificationType === 'whatsapp') {
    await sendWhatsappWithEmailFallback({
      institutionID,
      user: { _id: user._id, whatsappPhone: user.whatsappPhone!, email: user.email },
      mailTemplate,
      mailTemplateType,
      parameters: [
        user.title || '-',
        InstitutionMailTemplate.mapFirstNameVar(user) || '-',
        InstitutionMailTemplate.mapLastNameVar(user) || '-',
        participation.product?.name || '-',
        participation.waitingListPlace?.toString() || '-',
      ],
      sendEmail,
    });
  } else if (notificationType === 'email') {
    await sendEmail();
  } else {
    // no notification send because both are deactivated
    logger.warn(
      { notificationType, notification: mailTemplate.emailType },
      'no notification send because neither email nor whatsapp are activated',
    );
  }
}
